import { VISIBLE_CANDIDATES, type Activity } from '@sorted/types'
import * as ToggleGroup from '@radix-ui/react-toggle-group'
import { CalendarDays, Copy, Link2, MapPin, RotateCcw, Users } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  ACTIVITY_ICON,
  Check,
  ChevronRight,
  Send,
  Spinner,
} from '../components/icons.js'
import { AreaPicker } from '../components/AreaPicker.js'
import { OriginPicker, type PickedOrigin } from '../components/OriginPicker.js'
import { Head, Screen } from '../components/Screen.js'
import { Steps } from '../components/Steps.js'
import { ApiError, api, type Area } from '../lib/api.js'
import { fireConfetti } from '../lib/confetti.js'
import { activityLabel, isFutureLocal, meetingWhenLong, whatsappHref } from '../lib/format.js'
import { recallOrigin, rememberOrigin } from '../lib/geo.js'
import { t } from '../lib/i18n.js'

/**
 * Alice's creation screen - spec §2.
 *
 * Three steps: what, when, where. The last splits in two - which area, then
 * where she is starting from - because both answers are needed and asking them
 * on one screen makes the screen the decision rather than the questions.
 *
 * Only the date step
 * has a button, because a datetime input has no moment where the user is
 * unambiguously finished; everywhere else the choice IS the action, so there
 * is never a control that decides nothing.
 *
 * Nothing is pre-filled. A default activity or date looks decided when it is
 * not, and someone could send a link for a time they never chose.
 *
 * Invariant 3: she never sees a venue before sending, so there is nothing here
 * to preview.
 */

const ACTIVITIES: Activity[] = ['coffee', 'brunch', 'drinks', 'dinner', 'walk', 'beer_garden']

type Step = 1 | 2 | 3

/** Let the corridor decide, or pin it to a postcode area. */
type AreaMode = 'between' | 'area'

export function Create() {
  const [step, setStep] = useState<Step>(1)
  /** Highest step reached - the indicator will not jump ahead of the answers. */
  const [furthest, setFurthest] = useState<Step>(1)
  const [activity, setActivity] = useState<Activity | null>(null)
  const [when, setWhen] = useState('')
  /**
   * Which question step 3 is asking. Null until she chooses - like the
   * activity and the date, nothing here is pre-decided, because a default
   * 'between you' would be a choice she never made.
   */
  const [mode, setMode] = useState<AreaMode | null>(null)
  const [area, setArea] = useState<Area | null>(null)
  const [origin, setOrigin] = useState<PickedOrigin | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [share, setShare] = useState<{ url: string; token: string } | null>(null)
  const [copied, setCopied] = useState(false)

  /**
   * The link exists. Fired once, from an effect rather than from `create`:
   * this screen re-renders on every copy tap, and confetti that goes off again
   * each time stops reading as celebration.
   */
  const celebrated = useRef(false)
  useEffect(() => {
    if (share && !celebrated.current) {
      celebrated.current = true
      fireConfetti()
    }
  }, [share])

  const remembered = recallOrigin()
  const Icon = activity ? ACTIVITY_ICON[activity] : null

  function copyLink(url: string) {
    void navigator.clipboard?.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  /**
   * Throw this link away and begin again.
   *
   * The old meeting is discarded, not merely forgotten: leaving it live would
   * mean a link that may already be pasted somewhere still resolving to a
   * meeting that has been replaced.
   */
  function startOver() {
    const stale = share
    setShare(null)
    setActivity(null)
    setWhen('')
    setMode(null)
    setArea(null)
    setOrigin(null)
    setStep(1)
    setFurthest(1)
    if (stale) void api.discard(stale.token).catch(() => {})
  }

  /** Back to the area question. Clears the pick so the two cannot disagree. */
  function resetArea() {
    setMode(null)
    setArea(null)
  }

  function go(to: Step) {
    setStep(to)
    if (to > furthest) setFurthest(to)
  }

  /**
   * `from` is null for a pinned-area meeting - she gave no origin, because
   * the area already answers where. `pinned` is passed rather than read from
   * state: a setState in the same tick has not landed yet.
   */
  async function create(from: PickedOrigin | null, chosen: Activity, pinned?: Area) {
    setBusy(true)
    setError(null)
    try {
      if (from) rememberOrigin(from, from.label)
      const res = await api.create({
        activity: chosen,
        meetAt: new Date(when).toISOString(),
        bias: 'fair',
        constraints: [],
        ...(from ? { origin: { lat: from.lat, lng: from.lng } } : {}),
        ...(from?.label ? { originLabel: from.label } : {}),
        ...(pinned ? { areaId: pinned.id } : {}),
      })
      setShare({ url: res.shareUrl, token: res.token })
    } catch (err) {
      // Say WHAT went wrong. A generic "hat nicht geklappt" for a time in the
      // past sends someone to retry the one thing that cannot work - and the
      // server already tells us; we were throwing that away.
      const code = err instanceof ApiError ? err.code : ''
      setError(t(code === 'meet_at_in_past' ? 'create.timePast' : 'create.failed'))
      // Restore whichever picker was the last thing tapped - with no submit
      // button, leaving a spinner on screen would be a dead end.
      setOrigin(null)
      setArea(null)
      setBusy(false)
      // Send them back to the step that can actually fix it.
      if (code === 'meet_at_in_past') setStep(2)
    }
  }

  // ── done: the link ────────────────────────────────────────────────────
  if (share && activity) {
    const invite = [
      t('share.invite', {
        activity: activityLabel(activity),
        when: meetingWhenLong(new Date(when).toISOString()),
      }),
      share.url,
    ].join('\n')

    return (
      <Screen>
        <Head
          title={t('share.title')}
          lede={t('share.body', { n: String(VISIBLE_CANDIDATES) })}
        />

        {/* What the link says, stated as fact. These were tappable and are
            not any more: the link exists, so offering to change it means
            either leaving a stale link in the wild or silently killing one
            that may already be copied. Creating the link is the last step -
            getting it wrong means starting over, which is three taps. */}
        <div className="summary mt">
          {Icon && (
            <span className="fact">
              <Icon size={16} aria-hidden />
              {activityLabel(activity)}
            </span>
          )}
          <span className="fact">
            <CalendarDays size={16} aria-hidden />
            {meetingWhenLong(new Date(when).toISOString())}
          </span>
          <span className="fact">
            <MapPin size={16} aria-hidden />
            {area ? `${area.postcode} ${area.label}`.trim() : t('create.areaBetween')}
          </span>
        </div>

        {/* The URL looks tappable, so it copies. Anything that reads like a
            control should behave like one. */}
        <button className="card mt link-card" onClick={() => copyLink(share.url)}>
          <span className="faint link-row">
            {copied ? <Check size={16} aria-hidden /> : <Link2 size={16} aria-hidden />}
            {share.url}
          </span>
        </button>

        {/* What the link is for HER, as opposed to for the recipient: opening
            it shows her side, which is how she checks whether a decision has
            been made. Said plainly, it stops reading as a broken link. */}
        <p className="faint mt-sm">{t('share.ownLinkHint')}</p>

        <div className="stack mt">
          <a
            className="btn btn-primary"
            href={whatsappHref(invite)}
            target="_blank"
            rel="noreferrer"
          >
            <Send size={18} aria-hidden />
            {t('share.whatsapp')}
          </a>
          <button className="btn" onClick={() => copyLink(share.url)}>
            {copied ? <Check size={18} aria-hidden /> : <Copy size={17} aria-hidden />}
            {copied ? t('share.copied') : t('share.copy')}
          </button>

          {/* The only way back now that the facts are inert. Quiet, because
              it throws away a link that may already have been sent. */}
          <button className="btn btn-ghost" onClick={startOver}>
            <RotateCcw size={16} aria-hidden />
            {t('share.startOver')}
          </button>
        </div>
      </Screen>
    )
  }

  // ── the three steps ───────────────────────────────────────────────────
  const stepper = (
    <Steps
      current={step}
      furthest={furthest}
      onGo={setStep}
      labels={[t('create.stepWhat'), t('create.stepWhen'), t('create.stepWhere')]}
    />
  )

  if (step === 1) {
    return (
      <Screen>
        {stepper}

        {/* The question first, the sales pitch after.
        
            The headline used to open the screen and the actual question sat
            under it as a small label — so the first thing anyone saw was a
            claim rather than something to do. Someone who arrived to make a
            plan can now start; someone wondering what this is reads on.
            
            Same <Head> as steps 2 and 3: all three ask a question, so all
            three look the same asking it. */}
        {/* No lede: six labelled buttons need no sentence explaining them,
            and the other steps' ledes exist because a date field and a map
            do. */}
        <Head title={t('create.step1')} />

        <div className="field mt">
          <ToggleGroup.Root
            type="single"
            value={activity ?? ''}
            onValueChange={(v) => {
              if (!v) return
              setActivity(v as Activity)
              go(2)
            }}
            className="options"
            aria-label={t('create.step1')}
          >
            {ACTIVITIES.map((a) => {
              const ItemIcon = ACTIVITY_ICON[a]
              return (
                <ToggleGroup.Item key={a} value={a} className="option">
                  <ItemIcon size={22} strokeWidth={1.5} aria-hidden />
                  {activityLabel(a)}
                </ToggleGroup.Item>
              )
            })}
          </ToggleGroup.Root>
        </div>

        {/* What this is, for whoever needs it — below the choice, not in
            front of it. Headed as the question someone actually arrives
            with, because a stray link to a page of six buttons does not
            explain itself. */}
        <div className="promise">
          <p className="promise-what">{t('create.whatIsThis')}</p>
          <p className="promise-line">{t('create.headline')}</p>
          <p className="faint">{t('create.pitch')}</p>
          <p className="faint mt-sm">{t('create.dataSource')}</p>
        </div>
      </Screen>
    )
  }

  if (step === 2) {
    return (
      <Screen>
        {stepper}
        <Head title={t('create.step2')} lede={t('create.step2Hint')} />

        <div className="field mt">
          <input
            className="input"
            type="datetime-local"
            value={when}
            min={toLocalInput(new Date())}
            onChange={(e) => setWhen(e.target.value)}
            aria-label={t('create.step2')}
          />
        </div>

        {/* Say WHY the button is dead.
            A disabled control with no explanation asks someone to work out
            what is wrong with a date that looks perfectly fine to them. Only
            a time in the past is an error worth naming - an empty field is
            not a mistake, it is a step not taken yet, so it stays silent. */}
        {when !== '' && !isFutureLocal(when) && (
          <p className="faint error" role="status">
            {t('create.timePast')}
          </p>
        )}

        {/* The one step that needs a button: a datetime input has no moment
            where the user is unambiguously done, so nothing else can stand in
            for "yes, that time". */}
        {/* The input's `min` only constrains the picker UI - a typed or pasted
            value sails past it - so the future check lives here too. Without
            it the server rejects the meeting three taps later, after the
            location has already been shared. */}
        <button className="btn btn-primary" disabled={!isFutureLocal(when)} onClick={() => go(3)}>
          {t('create.next')}
          <ChevronRight size={18} aria-hidden />
        </button>
      </Screen>
    )
  }

  // ── step 3: where ─────────────────────────────────────────────────────
  //
  // The area question, and then - only for "between you" - where she is
  // setting off from. Pinning an area answers the question her origin was
  // being asked for, so asking it anyway would be collecting a location that
  // changes nothing.

  if (mode === null) {
    return (
      <Screen>
        {stepper}
        <Head title={t('create.step3')} lede={t('create.step3Hint')} />

        <div className="stack mt">
          <button className="btn" onClick={() => setMode('between')}>
            <Users size={18} aria-hidden />
            {t('create.areaBetween')}
          </button>
          <button className="btn" onClick={() => setMode('area')}>
            <MapPin size={18} aria-hidden />
            {t('create.areaPinned')}
          </button>
        </div>

        {/* Which of the two does what, said once, here. On the next screen
            the choice is already made and the explanation is too late. */}
        <p className="faint mt-sm">{t('create.areaHint')}</p>
      </Screen>
    )
  }

  // Pinning an area is the last answer needed, so it creates the link.
  // Asking her where she sets off from would be collecting a location that
  // changes neither the search nor anything she is shown.
  if (mode === 'area') {
    return (
      <Screen>
        {stepper}
        <Head title={t('create.areaTitle')} lede={t('create.areaSearchHint')} />

        {/* Stays mounted while the link is created: the picker owns its own
            confirm button, and tearing the map down to show a spinner would
            throw away a map that took a second to load. */}
        <div className="mt">
          <AreaPicker
            busy={busy}
            onPick={(a) => {
              if (!activity) return
              setArea(a)
              void create(null, activity, a)
            }}
          />
        </div>

        {error && (
          <p className="faint error mt" role="status">
            {error}
          </p>
        )}

        <button className="btn btn-ghost mt-sm" onClick={resetArea}>
          {t('create.areaChange')}
        </button>
      </Screen>
    )
  }

  return (
    <Screen>
      {stepper}
      <Head title={t('create.originTitle')} lede={t('create.originHint')} />

      {/* Disclosure above the buttons: picking an origin creates the link, so
          this has to be read before the tap, not after it. */}
      <p className="faint mt mb">{t('create.privacy')}</p>

      {busy ? (
        <button className="btn btn-primary" disabled>
          <Spinner />
          {t('create.working')}
        </button>
      ) : (
        <OriginPicker
          remembered={remembered}
          busy={busy}
          onPick={(o) => {
            if (!activity) return
            setOrigin(o)
            void create(o, activity)
          }}
        />
      )}

      {error && (
        <p className="faint error mt" role="status">
          {error}
        </p>
      )}

      <button className="btn btn-ghost mt-sm" onClick={resetArea}>
        {t('create.areaChange')}
      </button>
    </Screen>
  )
}

/** datetime-local wants local time with no timezone suffix. */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
